import React from "react";
import { LuUsers as Users, LuWallet as Wallet, LuInfo as Info } from "react-icons/lu";
import { Amount } from "src/components/Amount";
import { SessionWithUsers } from "src/features/sessions/types";

export const FinancialSection: React.FC<SessionWithUsers> = ({ session, users }) => {
  const learnerEntries = Object.entries(session.learnerBalanceDeducted ?? {});

  const totals = learnerEntries.reduce(
    (sum, [, amt]) => {
      sum.total += amt.amountDetails?.totalAmount || amt.amount;
      sum.vat += amt.amountDetails?.vatAmount || 0;
      sum.coach += amt.amountDetails?.coachAmount || 0;
      sum.fee += amt.amountDetails?.businessFee || 0;
      return sum;
    },
    { total: 0, vat: 0, coach: 0, fee: 0 }
  );

  const currency = learnerEntries[0]?.[1]?.currency;
  const coachAmt = session.coachEarnedBalance;

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
      <div className="rounded-2xl border-2 border-red-200 bg-gradient-to-br from-red-50 to-orange-50 shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-red-500 to-orange-500 p-4 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                <Users className="h-5 w-5" />
              </div>
              <h4 className="font-bold text-lg">Learner Deductions</h4>
            </div>
            {currency && (
              <span className="px-3 py-1 bg-white bg-opacity-20 backdrop-blur-sm rounded-full text-xs font-bold">
                {currency}
              </span>
            )}
          </div>
        </div>

        <div className="p-6">
          {learnerEntries.length === 0 ? (
            <div className="flex items-center gap-2 text-gray-500">
              <Info className="h-5 w-5" />
              <span>No deductions</span>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-white rounded-xl p-4 shadow-sm border border-red-100">
                <div className="text-sm text-gray-600 mb-1">Total Deducted</div>
                <div className="text-3xl font-bold text-gray-900">
                  <Amount amount={totals.total} currency={currency} />
                </div>
              </div>

              <div className="bg-white rounded-xl overflow-x-auto border border-red-100">
                <table className="w-full text-sm">
                  <thead className="bg-red-50">
                    <tr className="[&>th]:py-3 [&>th]:px-4 [&>th]:text-left [&>th]:text-xs [&>th]:font-bold [&>th]:text-gray-700">
                      <th>Learner</th>
                      <th className="text-right">Total</th>
                      <th className="text-right">VAT</th>
                      <th className="text-right">Coach</th>
                      <th className="text-right">Fee</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-red-50">
                    {learnerEntries.map(([learnerId, amt]) => {
                      const name = users[learnerId]?.name ?? learnerId;
                      return (
                        <tr key={learnerId} className="hover:bg-red-50 transition-colors">
                          <td className="py-3 px-4 font-medium text-gray-800">{name}</td>
                          <td className="py-3 px-4 text-right font-semibold">
                            <Amount amount={amt.amountDetails?.totalAmount || amt.amount} currency={amt.currency} />
                          </td>
                          <td className="py-3 px-4 text-right">
                            <Amount amount={amt.amountDetails?.vatAmount || 0} currency={amt.currency} />
                          </td>
                          <td className="py-3 px-4 text-right">
                            <Amount amount={amt.amountDetails?.coachAmount || 0} currency={amt.currency} />
                          </td>
                          <td className="py-3 px-4 text-right">
                            <Amount amount={amt.amountDetails?.businessFee || 0} currency={amt.currency} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="rounded-2xl border-2 border-green-200 bg-gradient-to-br from-green-50 to-emerald-50 shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-green-500 to-emerald-500 p-4 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                <Wallet className="h-5 w-5" />
              </div>
              <h4 className="font-bold text-lg">Coach Earnings</h4>
            </div>
            {coachAmt && (
              <span className="px-3 py-1 bg-white bg-opacity-20 backdrop-blur-sm rounded-full text-xs font-bold">
                {coachAmt.currency}
              </span>
            )}
          </div>
        </div>

        <div className="p-6">
          {!coachAmt ? (
            <div className="flex items-center gap-2 text-gray-500">
              <Info className="h-5 w-5" />
              <span>No earnings</span>
            </div>
          ) : (
            <div className="bg-white rounded-xl p-6 shadow-sm border border-green-100">
              <div className="text-sm text-gray-600 mb-2">Total Earned</div>
              <div className="text-4xl font-bold text-green-600">
                <Amount
                  amount={coachAmt.amountDetails?.coachAmount ?? coachAmt.amount ?? 0}
                  currency={coachAmt.currency}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
